import {
  Button,
  DatePicker,
  Flex,
  Input,
  Select,
  Space,
  Switch,
  TableProps,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { Link } from "react-router-dom";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import TitleBar from "../../../../components/common/TitleBar";
import CustomTable from "../../../../components/ui/CustomTable";
import { dbDateFormat } from "../../../../constants/constants";
import {
  AllShiftParams,
  Shift,
} from "../../../../features/organization/contractual/ShiftManagement/shiftManagement.type";
import {
  useGetAllShiftQuery,
  useUpdateShiftMutation,
} from "../../../../features/organization/contractual/ShiftManagement/shiftManagementApi";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate, parseTime } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../../utils/number";
import { capitalize, valueOrNa } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";
import UsersIcon from "../../dashboard/_icons/UsersIcon";
import CalanderIcon from "../../survey/templates/_icons/CalanderIcon";
import ManageShift from "./_components/ManageShift";
const { RangePicker } = DatePicker;

const ShiftList = () => {
  const { open, handleOpen, handleClose } = useModal();
  const [updatingRow, setUpdatingRow] = useState<number | null>(null);
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleSearch,
    handleFilter,
    handlePagination,
  } = useTable<AllShiftParams>();

  // --- Query ---
  const { data, isLoading, isFetching } = useGetAllShiftQuery({
    params: { ...filter, search: keyword },
  });

  // --- Mutations ---
  const [updateStatus] = useUpdateShiftMutation();

  const handleStatusChange = async (record: Shift, newStatus: string) => {
    setUpdatingRow(record.id);
    try {
      await updateStatus({
        id: record.id,
        data: { status: newStatus },
      }).unwrap();
      toast("success", "Shift status updated successfully.");
    } catch (err) {
      console.error(err);
      toast("error", "Status update failed");
    } finally {
      setUpdatingRow(null);
    }
  };

  // --- Table Columns ---
  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Shift Name",
      dataIndex: "shift_name",
      render: (val) => valueOrNa(val),
    },
    {
      align: "center",
      title: "Number of Seat",
      dataIndex: "number_of_seat",
      render: (val) => `${addCommaInNumber(val || 0)}`,
    },
    {
      align: "center",
      title: "Date Range",
      render: (record) =>
        `${formatDate(record?.from_date, "DD")}–${formatDate(record?.to_date)}`,
    },
    {
      align: "center",
      title: "Time",
      render: (record) => {
        const startTime = parseTime(record?.shift_start_time);
        const endTime = parseTime(record?.shift_end_time);

        return `${startTime ?? "-"} – ${endTime ?? "-"}`;
      },
    },
    {
      align: "center",
      title: "Number of Employee",
      dataIndex: "number_of_employee",
      render: (val) => addCommaInNumber(val || 0),
    },
    {
      align: "center",
      title: "Status",
      dataIndex: "status",
      render: (val, record) => (
        <Flex gap={6} align="center">
          <Switch
            loading={updatingRow === record.id}
            onChange={() =>
              handleStatusChange(
                record,
                val === "active" ? "inactive" : "active"
              )
            }
            checked={val === "active"}
            className="bg-active"
          />
          <span>{capitalize(val)}</span>
        </Flex>
      ),
    },
    {
      title: "Action",
      align: "center",
      width: 130,
      render: (record) => (
        <Space align="center">
          <Tooltip title="View Attendance">
            <Link to={`employees/${record.id}`}>
              <Button
                className="action-view"
                size="small"
                icon={<UsersIcon height={19} width={18} />}
              />
            </Link>
          </Tooltip>
          <Tooltip title="View Employees">
            <Link to={`view/${record.id}`}>
              <Button
                className="action-view"
                size="small"
                icon={<ViewIcon />}
              />
            </Link>
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              onClick={() => handleOpen({ type: "UPDATE", data: record })}
              className="action-edit"
              size="small"
              icon={<EditIcon />}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <SidebarLayout title="Shift Management">
      <TitleBar title="Shift List">
        <Button
          onClick={() => handleOpen({ type: "ADD" })}
          color="primary"
          type="primary"
        >
          <AddIcon /> New Shift
        </Button>
      </TitleBar>
      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data.total}
        dataSource={addKeyInArray(data?.data.shifts)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
              allowClear
            />

            <RangePicker
              style={{ width: 245 }}
              separator="-"
              className="w-full input-bordered"
              format={"DD/MM/YYYY"}
              suffixIcon={<CalanderIcon />}
              placeholder={["dd/mm/yyyy", "dd/mm/yyyy"]}
              onChange={(dates: (Dayjs | null)[] | null) => {
                const [start, end] = dates || [];

                // Call filter only if both dates are selected
                if (start && end) {
                  handleFilter("from_date", dayjs(start).format(dbDateFormat));
                  handleFilter("to_date", dayjs(end).format(dbDateFormat));
                } else {
                  // Clear filters if user clears dates
                  handleFilter("from_date", undefined);
                  handleFilter("to_date", undefined);
                }
              }}
            />

            <Select
              allowClear
              placeholder="Status"
              className="min-w-[90px]"
              rootClassName="icon-gray"
              popupMatchSelectWidth={120}
              suffixIcon={<SelectDownIcon />}
              onChange={(value) => handleFilter("status", value)}
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
            />
          </>
        }
      />

      <ManageShift
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        open={open.open && (open.type === "ADD" || open.type === "UPDATE")}
      />
    </SidebarLayout>
  );
};

export default ShiftList;
